import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createDonation,
  createInvoice,
  createPayment,
  fetchBillingOverview,
  fetchPlans,
  fetchQuota,
  fetchSubscription,
  type BillingOverview,
  type CreateInvoiceArgs,
  type CreatePaymentResult,
  type PlanInfo,
  type PlanTier,
  type Quota,
  type Subscription,
} from '../api/planApi';

export const planKeys = {
  list: ['plans', 'list'] as const,
  quota: ['plans', 'quota'] as const,
  subscription: ['plans', 'subscription'] as const,
  billing: ['plans', 'billing'] as const,
};

interface QueryOptions {
  enabled?: boolean;
}

export function usePlansQuery(options: QueryOptions = {}) {
  return useQuery<PlanInfo[], Error>({
    queryKey: planKeys.list,
    queryFn: fetchPlans,
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

export function useQuotaQuery(options: QueryOptions = {}) {
  return useQuery<Quota, Error>({
    queryKey: planKeys.quota,
    queryFn: fetchQuota,
    enabled: options.enabled ?? true,
    // Короткий staleTime: индикатор должен освежаться после каждой генерации.
    staleTime: 15_000,
  });
}

export function useSubscriptionQuery(options: QueryOptions = {}) {
  return useQuery<Subscription, Error>({
    queryKey: planKeys.subscription,
    queryFn: fetchSubscription,
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

/** Создаёт подписку и редиректит браузер на страницу оплаты YooKassa.
 *  Редирект — full-page (window.location), т.к. confirmationUrl внешний. */
export function useCreatePaymentMutation() {
  return useMutation<CreatePaymentResult, Error, PlanTier>({
    mutationFn: createPayment,
    onSuccess: ({ confirmationUrl }) => {
      window.location.href = confirmationUrl;
    },
  });
}

/** Создаёт донат и редиректит на страницу оплаты YooKassa. */
export function useCreateDonationMutation() {
  return useMutation<CreatePaymentResult, Error, number>({
    mutationFn: createDonation,
    onSuccess: ({ confirmationUrl }) => {
      window.location.href = confirmationUrl;
    },
  });
}

/** Единый payload помесячной модели «Тарифы» (текущий период, навигатор месяцев,
 *  цены, история, донат). Короткий staleTime — данные меняются после оплаты. */
export function useBillingOverviewQuery(options: QueryOptions = {}) {
  return useQuery<BillingOverview, Error>({
    queryKey: planKeys.billing,
    queryFn: fetchBillingOverview,
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

/** Выставляет счёт за выбранный месяц/тариф и редиректит на страницу оплаты
 *  YooKassa (full-page redirect — confirmationUrl внешний). */
export function useCreateInvoiceMutation() {
  return useMutation<CreatePaymentResult, Error, CreateInvoiceArgs>({
    mutationFn: createInvoice,
    onSuccess: ({ confirmationUrl }) => {
      window.location.href = confirmationUrl;
    },
  });
}
